import { Button, Icons } from "@sembark-travel/ui-base"
import { useDialog } from "@sembark-travel/ui-dialog"
import { SendTripWhatsAppDialog } from "../Trips/WhatsApp"
import { useTripWhatsApp } from "../Trips/store"
import { useCanSendOnWhatsApp } from "../WhatsAppMessages"

/**
 * Send the guest's operational-bookings summary through the tenant's own
 * WhatsApp integration, beside the wa.me share that hands off to the app.
 *
 * Text only — these are quick "here is your day" messages, so there is no
 * document to attach and no template parameters to override.
 */
export function SendOperationalBookingsOnWhatsApp({
	tripId,
	message,
	disabled,
	fallback = null,
}: {
	tripId: number
	message: string
	disabled?: boolean
	// Rendered instead when this tenant has no WhatsApp integration — the
	// wa.me hand-off, so the share tab is never left without a send action.
	fallback?: React.ReactNode
}) {
	const canSendOnWhatsApp = useCanSendOnWhatsApp()
	const { overview, error, refresh } = useTripWhatsApp(
		canSendOnWhatsApp ? tripId : null
	)
	const [isDialogOpen, openDialog, closeDialog] = useDialog()

	// No integration means no fetch and no button — the tab keeps its wa.me
	// flow on its own.
	if (!canSendOnWhatsApp || error || !overview) {
		return <>{fallback}</>
	}

	const missingSender = !overview.phone_numbers.length
	const missingRecipient = !overview.contact.phone_number_plain
	const blockedReason = missingSender
		? "No WhatsApp phone number is connected."
		: missingRecipient
			? "This trip has no customer phone number."
			: undefined

	return (
		<>
			<Button
				level="primary"
				status="success"
				disabled={disabled || Boolean(blockedReason)}
				title={blockedReason}
				onClick={openDialog}
			>
				<Icons.WhatsApp /> Send via WhatsApp
			</Button>
			<SendTripWhatsAppDialog
				open={isDialogOpen}
				onClose={closeDialog}
				tripId={tripId}
				overview={overview}
				title="Send Bookings on WhatsApp"
				description="Edit the message before sending. A template is optional; when chosen it is sent first, then your message."
				initialCustomMessage={message}
				onSuccess={async () => {
					await refresh()
					closeDialog()
				}}
			/>
		</>
	)
}
